import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyIcon from '@mui/icons-material/Key';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  AttributeOption,
  InternalSkuOption,
  PurchaseLine,
  PurchaseLineSku,
  createEmptySku,
  hasMissingSkuProductData,
} from '../../types/purchasing';
import {
  createAttributeValue,
  fetchCatalogProductSkus,
  fetchNextAttributeNumber,
} from '../../services/purchasingService';
import { filterAttributeOptionsBySearchRank } from './attributeOptionFilter';

interface SizeMatrixProps {
  line: PurchaseLine;
  attribute1Options: AttributeOption[];
  attribute2Options: AttributeOption[];
  attribute3Options: AttributeOption[];
  deliveryMonths?: { value: string; label: string; short?: string }[];
  onChange: (line: PurchaseLine) => void;
  onAttribute1Created?: (attribute: AttributeOption) => void | Promise<void>;
  readOnly?: boolean;
}

interface SizeCurvePreset {
  label: string;
  values: number[];
}

interface SizeTemplate {
  label: string;
  sizes: string[];
  lengths?: string[];
}

const SIZE_TEMPLATES: SizeTemplate[] = [
  { label: 'Kläder – Vuxen', sizes: ['S', 'M', 'L', 'XL', 'XXL', '3XL'] },
  {
    label: 'Skor – Dam',
    sizes: ['36', '37', '38', '39', '40', '41', '42'],
  },
  {
    label: 'Skor – Herr',
    sizes: ['40', '41', '42', '43', '44', '45', '46'],
  },
  {
    label: 'Kläder – Barn',
    sizes: [
      '86/92',
      '98/104',
      '110/116',
      '122/128',
      '134/140',
      '146/152',
      '158/164',
      '170/176',
    ],
  },
  {
    label: 'Skor – Barn',
    sizes: Array.from({ length: 16 }, (_, i) => String(20 + i)),
  },
  {
    label: 'Jeans – Dam',
    sizes: ['26', '27', '28', '29', '30', '31', '32', '33', '34'],
    lengths: ['30', '32'],
  },
  {
    label: 'Jeans – Herr',
    sizes: ['28', '29', '30', '31', '32', '33', '34'],
    lengths: ['30', '32', '34', '36'],
  },
];

const ALL_KNOWN_SIZES = Array.from(
  new Set(SIZE_TEMPLATES.flatMap((t) => t.sizes)),
);

type VariantKey = string; // "size" or "size|length"

function buildVariantKey(size: string, length?: string | null): VariantKey {
  const s = (size || '').trim();
  const l = (length || '').trim();
  return l ? `${s}|${l}` : s;
}

function parseVariantKey(key: VariantKey): {
  size: string;
  length: string | null;
} {
  const raw = (key || '').trim();
  if (!raw) return { size: '', length: null };
  if (raw.includes('|')) {
    const [size, length] = raw.split('|', 2).map((p) => p.trim());
    return { size: size || '', length: length || null };
  }
  return { size: raw, length: null };
}

function parseVariantInput(
  rawInput: string,
  allowSlashLength: boolean,
): {
  size: string;
  length: string | null;
} {
  const raw = (rawInput || '').trim();
  if (!raw) return { size: '', length: null };
  if (raw.includes('|')) return parseVariantKey(raw);
  // In two-dimensional mode, users may type "30/32" for waist/length.
  // Sizes like "41 1/3" and "86/92" must remain single size codes.
  if (
    allowSlashLength &&
    /^\S+\s*\/\s*\S+$/.test(raw) &&
    !/^\d+\s+\d+\/\d+$/.test(normalizeSizeSortText(raw))
  ) {
    const [size, length] = raw.split('/', 2).map((p) => p.trim());
    return { size: size || '', length: length || null };
  }
  return { size: raw, length: null };
}

function formatVariantLabel(key: VariantKey): string {
  const { size, length } = parseVariantKey(key);
  return length ? `${size}/${length}` : size;
}

function normalizeSizeSortText(raw: string): string {
  return (raw || '')
    .toString()
    .trim()
    .replace(/[\u00A0\u1680\u180E\u2000-\u200D\u202F\u205F\u3000\uFEFF]/g, ' ')
    .replace(/[ \t\r\n]+/g, ' ');
}

function parseSortableNumber(raw: string): number | null {
  const s = normalizeSizeSortText(raw).replace(',', '.');
  if (!s) return null;
  const mixed = s.match(/^(-?\d+(?:\.\d+)?)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const numerator = Number(mixed[2]);
    const denominator = Number(mixed[3]);
    if (
      Number.isFinite(whole) &&
      Number.isFinite(numerator) &&
      Number.isFinite(denominator) &&
      denominator !== 0
    ) {
      return whole + numerator / denominator;
    }
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function cmpMaybeNumber(a: string, b: string): number {
  const an = parseSortableNumber(a);
  const bn = parseSortableNumber(b);
  if (an != null && bn != null) return an - bn;
  if (an != null) return -1;
  if (bn != null) return 1;
  return a.localeCompare(b, 'sv-SE', { numeric: true, sensitivity: 'base' });
}

function commonSizeRankFromTemplates(): Map<string, number> {
  // Higher rank => earlier in list. Derived from existing templates so we
  // prioritise "common" sizes without hardcoding more lists.
  const freq = new Map<string, number>();
  for (const t of SIZE_TEMPLATES) {
    for (const s of t.sizes) {
      const key = (s || '').trim();
      if (!key) continue;
      freq.set(key, (freq.get(key) || 0) + 1);
    }
  }

  const sorted = Array.from(freq.entries()).sort((a, b) => {
    const f = b[1] - a[1];
    if (f !== 0) return f;
    return cmpMaybeNumber(a[0], b[0]);
  });

  const rank = new Map<string, number>();
  // Explicit "most common" clothing sizes first.
  // These should always stay at the top, regardless of template frequency.
  const pinned = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];
  for (let i = 0; i < pinned.length; i++) {
    // Large numbers win; keep plenty of space above derived ranks.
    rank.set(pinned[i], 1000 - i);
  }

  // Assign descending ranks so "bigger is better".
  for (let i = 0; i < sorted.length; i++) {
    const key = sorted[i][0];
    if (rank.has(key)) continue;
    rank.set(key, sorted.length - i);
  }
  return rank;
}

const COMMON_SIZE_RANK = commonSizeRankFromTemplates();

function cmpSizeCode(a: string, b: string): number {
  const an = parseSortableNumber(a);
  const bn = parseSortableNumber(b);
  if (an != null && bn != null) return an - bn;

  const ra = COMMON_SIZE_RANK.get((a || '').trim()) || 0;
  const rb = COMMON_SIZE_RANK.get((b || '').trim()) || 0;
  if (ra !== rb) return rb - ra;
  return cmpMaybeNumber(a, b);
}

function sortTwoDimVariantKeys(keys: VariantKey[]): VariantKey[] {
  return [...keys].sort((ak, bk) => {
    const a = parseVariantKey(ak);
    const b = parseVariantKey(bk);
    // group by length first (30, 32, 34...), then size
    const lc = cmpMaybeNumber(a.length || '', b.length || '');
    if (lc !== 0) return lc;
    return cmpSizeCode(a.size, b.size);
  });
}

function buildPresets(variantKeys: VariantKey[]): SizeCurvePreset[] {
  const n = variantKeys.length;
  if (n === 0) return [];
  const presets: SizeCurvePreset[] = [
    { label: `Flat ×2`, values: Array(n).fill(2) },
    { label: `Flat ×3`, values: Array(n).fill(3) },
  ];
  // Bell curve
  if (n >= 3) {
    const mid = Math.floor(n / 2);
    const bell = Array.from({ length: n }, (_, i) => {
      const dist = Math.abs(i - mid);
      return Math.max(1, mid - dist + 1);
    });
    presets.unshift({ label: `Bell ${bell.join('-')}`, values: bell });
  }
  return presets;
}

const numberValue = (raw: string): number | null => {
  if (!raw.trim()) return null;
  const parsed = Number(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const skuLockKey = (sku: PurchaseLineSku, index: number): string => {
  if (sku.purchaseLineSkuId != null)
    return `purchase-line-sku-${sku.purchaseLineSkuId}`;
  if (sku.sourceSkuId != null) return `source-sku-${sku.sourceSkuId}`;
  return [
    'local-sku',
    index,
    sku.attribute1Code || '',
    sku.attribute2Code || '',
    sku.attribute3Code || '',
  ].join('|');
};

type ColorAutocompleteValue = AttributeOption | string | null;

const colorCodeFromAutocompleteValue = (
  value: ColorAutocompleteValue,
): string => {
  if (!value) return '';
  return typeof value === 'string' ? value.trim() : value.code.trim();
};

const normalizeAttributeLookupKey = (raw: string | null | undefined): string =>
  (raw || '')
    .toString()
    .trim()
    .replace(/[\u2044\u2215\uFF0F]/g, '/')
    .replace(/[\u00A0\u1680\u180E\u2000-\u200D\u202F\u205F\u3000\uFEFF]/g, ' ')
    .replace(/[\u200B-\u200D\u2060]/g, '')
    .replace(/[ \t\r\n]+/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .toLowerCase();

const SizeMatrix: React.FC<SizeMatrixProps> = ({
  line,
  attribute1Options,
  attribute2Options,
  attribute3Options,
  deliveryMonths = [],
  onChange,
  onAttribute1Created,
  readOnly = false,
}) => {
  const cellRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const eanInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const effectiveBulkPrice = line.bulkPrice ?? line.bulkPriceOrig ?? null;

  // For system-matched (internal) lines we can auto-fill SKU data.
  const isInternalLine = line.source === 'internal';
  const productNumber = (line.articleNumber || '').trim();

  const [systemSkus, setSystemSkus] = useState<InternalSkuOption[]>([]);
  const [systemSkusLoading, setSystemSkusLoading] = useState(false);
  const systemSkusRequestIdRef = useRef(0);

  // Dialogs / menus
  const [addColorOpen, setAddColorOpen] = useState(false);
  const [addColorValue, setAddColorValue] =
    useState<ColorAutocompleteValue>(null);
  const [addColorTemplateLabel, setAddColorTemplateLabel] = useState<
    string | null
  >(null);
  const [customTemplateOpen, setCustomTemplateOpen] = useState(false);
  const [customSizes, setCustomSizes] = useState<string[]>([]);
  const [customUseLengths, setCustomUseLengths] = useState(false);
  const [customLengths, setCustomLengths] = useState<string[]>([]);
  const [rowMenu, setRowMenu] = useState<{
    el: HTMLElement;
    colorCode: string;
  } | null>(null);
  const [curveColor, setCurveColor] = useState<string | null>(null);
  const [curvePattern, setCurvePattern] = useState('1,2,3');
  const [fillColor, setFillColor] = useState<string | null>(null);
  const [fillValue, setFillValue] = useState('');
  const [dataDialogColor, setDataDialogColor] = useState<string | null>(null);
  const [dataColorCampaignEnabled, setDataColorCampaignEnabled] =
    useState(false);
  const [createColorCode, setCreateColorCode] = useState<string | null>(null);
  const [createColorNumber, setCreateColorNumber] = useState('');
  const [createColorLoadingNumber, setCreateColorLoadingNumber] =
    useState(false);
  const [createColorSaving, setCreateColorSaving] = useState(false);
  const [createColorError, setCreateColorError] = useState<string | null>(null);
  const [editColorCode, setEditColorCode] = useState<string | null>(null);
  const [editColorValue, setEditColorValue] = useState('');
  const [editColorError, setEditColorError] = useState<string | null>(null);
  const [unlockedEanKeys, setUnlockedEanKeys] = useState<Set<string>>(
    () => new Set(),
  );

  // Add length for a single color row (two-dim only)
  const [addLengthColor, setAddLengthColor] = useState<string | null>(null);
  const [addLengthValue, setAddLengthValue] = useState<string>('');

  // Insert size
  const [insertAnchorEl, setInsertAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [insertAtPosition, setInsertAtPosition] = useState<number | null>(null);
  const [insertSizeInput, setInsertSizeInput] = useState('');
  const [insertPopoverKey, setInsertPopoverKey] = useState(0);
  const [insertLockedLength, setInsertLockedLength] = useState<string | null>(
    null,
  );

  // Convert single-dim -> two-dim by assigning a single length to all existing SKUs
  const [convertLength, setConvertLength] = useState<string>('');

  // Remove size
  const [removeSizeCode, setRemoveSizeCode] = useState<string | null>(null);

  useEffect(() => {
    setUnlockedEanKeys((current) => {
      const activeKeys = new Set(
        line.skus
          .map((sku, idx) => (sku.ean13?.trim() ? skuLockKey(sku, idx) : null))
          .filter((key): key is string => Boolean(key)),
      );
      const next = new Set<string>();
      let changed = false;

      current.forEach((key) => {
        if (activeKeys.has(key)) {
          next.add(key);
        } else {
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [line.skus]);

  const lockEanField = (key: string) => {
    setUnlockedEanKeys((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const markEanFieldUnlocked = (key: string) => {
    setUnlockedEanKeys((current) => {
      if (current.has(key)) return current;
      const next = new Set(current);
      next.add(key);
      return next;
    });
  };

  const unlockEanField = (key: string) => {
    markEanFieldUnlocked(key);

    window.setTimeout(() => {
      const input = eanInputRefs.current[key];
      input?.focus();
      input?.select();
    }, 0);
  };

  const openInsertPopover = (el: HTMLElement, pos: number) => {
    setInsertAnchorEl(el);
    setInsertAtPosition(pos);
    setInsertSizeInput('');
    if (isTwoDim) {
      const keyAtPos =
        pos < variantKeys.length ? variantKeys[pos] : variantKeys.at(-1);
      const length = keyAtPos ? parseVariantKey(keyAtPos).length : null;
      setInsertLockedLength(length || null);
    } else {
      setInsertLockedLength(null);
    }
    setInsertPopoverKey((k) => k + 1);
  };

  // Derive color rows: unique attribute1Codes in order of first appearance
  const colorCodes = useMemo(() => {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const sku of line.skus) {
      const code = sku.attribute1Code || '';
      if (code && !seen.has(code)) {
        seen.add(code);
        order.push(code);
      }
    }
    return order;
  }, [line.skus]);

  const attribute1CodeLookup = useMemo(() => {
    const exact = new Set<string>();
    const normalized = new Set<string>();
    attribute1Options.forEach((option) => {
      const code = option.code?.trim();
      if (!code) return;
      exact.add(code);
      normalized.add(normalizeAttributeLookupKey(code));
    });
    return { exact, normalized };
  }, [attribute1Options]);

  const isColorInSystem = (colorCode: string): boolean => {
    const code = colorCode.trim();
    if (!code) return true;
    return (
      attribute1CodeLookup.exact.has(code) ||
      attribute1CodeLookup.normalized.has(normalizeAttributeLookupKey(code))
    );
  };

  // Derive columns: either attribute2-only (SingleDim) or attribute2+attribute3 (TwoDim),
  // in the order they appear in the skus array.
  const variantKeys = useMemo<VariantKey[]>(() => {
    const seen = new Set<VariantKey>();
    const order: VariantKey[] = [];
    for (const sku of line.skus) {
      const size = (sku.attribute2Code || '').trim();
      if (!size) continue;
      const length = (sku.attribute3Code || '').trim() || null;
      const key = buildVariantKey(size, length);
      if (!seen.has(key)) {
        seen.add(key);
        order.push(key);
      }
    }
    const hasLength = order.some((k) => Boolean(parseVariantKey(k).length));
    return hasLength ? sortTwoDimVariantKeys(order) : order;
  }, [line.skus]);

  const isTwoDim = useMemo(
    () => variantKeys.some((k) => Boolean(parseVariantKey(k).length)),
    [variantKeys],
  );
  const productWeight = useMemo(() => {
    if ((line.skus?.length || 0) === 0) return null;
    const weights = line.skus.map((sku) => {
      const weight = Number(sku.weight);
      return Number.isFinite(weight) && weight > 0 ? weight : null;
    });
    const first = weights[0] ?? null;
    return weights.every((weight) => weight === first) ? first : null;
  }, [line.skus]);

  const systemSkuIndex = useMemo(() => {
    const map = new Map<string, InternalSkuOption>();
    for (const sku of systemSkus) {
      const a1 = (sku.attribute1Code || '').trim();
      const a2 = (sku.attribute2Code || '').trim();
      const a3 = (sku.attribute3Code || '').trim();
      if (!a1 || !a2) continue;
      const key = `${a1}|${a2}|${a3}`;
      map.set(key, sku);
    }
    return map;
  }, [systemSkus]);

  useEffect(() => {
    if (!isInternalLine || !productNumber) {
      setSystemSkus([]);
      setSystemSkusLoading(false);
      return;
    }

    const requestId = ++systemSkusRequestIdRef.current;
    setSystemSkusLoading(true);
    (async () => {
      try {
        const skus = await fetchCatalogProductSkus(productNumber);
        if (systemSkusRequestIdRef.current === requestId) {
          setSystemSkus(skus);
        }
      } catch (error) {
        if (systemSkusRequestIdRef.current === requestId) {
          setSystemSkus([]);
        }
      } finally {
        if (systemSkusRequestIdRef.current === requestId) {
          setSystemSkusLoading(false);
        }
      }
    })();
  }, [isInternalLine, productNumber]);

  const hydrateFromSystemSku = (sku: PurchaseLineSku): PurchaseLineSku => {
    if (!isInternalLine) return sku;
    const a1 = (sku.attribute1Code || '').trim();
    const a2 = (sku.attribute2Code || '').trim();
    const a3 = (sku.attribute3Code || '').trim();
    if (!a1 || !a2) return sku;
    const match = systemSkuIndex.get(`${a1}|${a2}|${a3}`) || null;
    if (!match) return sku;
    return {
      ...sku,
      sourceSkuId: match.skuId,
      pluNo: match.priceLookupUnitNumber || sku.pluNo,
      ean13: match.europeanArticleNumber ?? sku.ean13 ?? null,
      weight: sku.weight ?? match.weight ?? null,
      // Keep purchase prices as-entered in the order; they are order-specific.
    };
  };

  const systemSizeCodes = useMemo(() => {
    const base =
      attribute2Options.length > 0
        ? attribute2Options.map((o) => o.code)
        : ALL_KNOWN_SIZES;
    const seen = new Set<string>();
    const deduped: string[] = [];
    for (const s of base) {
      const v = (s || '').trim();
      if (!v || seen.has(v)) continue;
      seen.add(v);
      deduped.push(v);
    }
    return deduped.sort(cmpSizeCode);
  }, [attribute2Options]);

  const systemLengthCodes = useMemo(() => {
    const base = attribute3Options.map((o) => (o.code || '').trim());
    const seen = new Set<string>();
    const deduped: string[] = [];
    for (const s of base) {
      if (!s || s.startsWith('"') || seen.has(s)) continue;
      seen.add(s);
      deduped.push(s);
    }
    return deduped.sort(cmpMaybeNumber);
  }, [attribute3Options]);

  const cellSku = (colorCode: string, variantKey: VariantKey) => {
    const { size, length } = parseVariantKey(variantKey);
    return line.skus.find(
      (s) =>
        s.attribute1Code === colorCode &&
        (s.attribute2Code || '') === size &&
        (length ? (s.attribute3Code || '') === length : !s.attribute3Code),
    );
  };

  const updateCell = (
    colorCode: string,
    variantKey: VariantKey,
    quantity: number,
  ) => {
    const { size, length } = parseVariantKey(variantKey);
    const nextSkus = line.skus.map((s) =>
      s.attribute1Code === colorCode &&
      (s.attribute2Code || '') === size &&
      (length ? (s.attribute3Code || '') === length : !s.attribute3Code)
        ? { ...s, quantity }
        : s,
    );
    onChange({ ...line, skus: nextSkus });
  };

  const rowTotal = (colorCode: string) =>
    line.skus
      .filter((s) => s.attribute1Code === colorCode)
      .reduce((sum, s) => sum + (s.quantity || 0), 0);

  const colTotal = (variantKey: VariantKey) => {
    const { size, length } = parseVariantKey(variantKey);
    return line.skus
      .filter(
        (s) =>
          (s.attribute2Code || '') === size &&
          (length ? (s.attribute3Code || '') === length : !s.attribute3Code),
      )
      .reduce((sum, s) => sum + (s.quantity || 0), 0);
  };

  const grandTotal = line.skus.reduce((sum, s) => sum + (s.quantity || 0), 0);

  // Keyboard navigation: arrow keys + enter + tab
  const handleCellKey = (
    e: React.KeyboardEvent<HTMLInputElement>,
    ci: number,
    si: number,
  ) => {
    const focus = (r: number, c: number) => {
      const el = cellRefs.current[`${colorCodes[r]}-${variantKeys[c]}`];
      if (el) {
        e.preventDefault();
        el.focus();
        el.select();
      }
    };
    if (e.key === 'ArrowRight') {
      if (si < variantKeys.length - 1) focus(ci, si + 1);
    } else if (e.key === 'ArrowLeft') {
      if (si > 0) focus(ci, si - 1);
    } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
      if (ci < colorCodes.length - 1) focus(ci + 1, si);
    } else if (e.key === 'ArrowUp') {
      if (ci > 0) focus(ci - 1, si);
    }
  };

  const addColor = () => {
    const code = colorCodeFromAutocompleteValue(addColorValue);
    if (!code) return;
    if (colorCodes.includes(code)) {
      setAddColorOpen(false);
      return;
    }

    let keysToAdd: VariantKey[];
    if (variantKeys.length > 0) {
      // Reuse whatever columns already exist in the matrix
      keysToAdd = variantKeys;
    } else if (line.source !== 'manual' && attribute2Options.length > 0) {
      // API-sourced line: auto-populate sizes (no length)
      keysToAdd = attribute2Options
        .slice(0, 6)
        .map((o) => buildVariantKey(o.code));
    } else {
      // Manual line: require an explicit template choice
      const cleanedSizes = Array.from(
        new Set(customSizes.map((s) => (s || '').trim()).filter(Boolean)),
      ).sort(cmpSizeCode);
      const cleanedLengths = Array.from(
        new Set(customLengths.map((s) => (s || '').trim()).filter(Boolean)),
      ).sort(cmpMaybeNumber);

      if (cleanedSizes.length > 0) {
        if (customUseLengths && cleanedLengths.length > 0) {
          // length-major order: all */30 first, then */32, etc.
          keysToAdd = cleanedLengths.flatMap((l) =>
            cleanedSizes.map((s) => buildVariantKey(s, l)),
          );
        } else {
          keysToAdd = cleanedSizes.map((s) => buildVariantKey(s));
        }
      } else {
        const tmpl = SIZE_TEMPLATES.find(
          (t) => t.label === addColorTemplateLabel,
        );
        if (!tmpl) return;
        if (tmpl.lengths && tmpl.lengths.length > 0) {
          // length-major order: all */30 first, then */32, etc.
          keysToAdd = tmpl.lengths.flatMap((l) =>
            tmpl.sizes.map((s) => buildVariantKey(s, l)),
          );
        } else {
          keysToAdd = tmpl.sizes.map((s) => buildVariantKey(s));
        }
      }
    }

    if (keysToAdd.length === 0) return;

    const newSkus: PurchaseLineSku[] = keysToAdd.map((key) => {
      const { size, length } = parseVariantKey(key);
      const base: PurchaseLineSku = {
        ...createEmptySku(line.articleNumber, line.productName),
        attribute1Code: code,
        attribute2Code: size,
        attribute3Code: length,
        supplierPrice: line.bulkSupplierPrice ?? null,
        priceOrig: line.bulkPriceOrig ?? null,
        price: effectiveBulkPrice,
        weight: productWeight,
      };
      return hydrateFromSystemSku(base);
    });

    onChange({ ...line, skus: [...line.skus, ...newSkus] });
    setAddColorOpen(false);
    setAddColorValue(null);
    setAddColorTemplateLabel(null);
    setCustomSizes([]);
    setCustomUseLengths(false);
    setCustomLengths([]);
  };

  const normalizeInsertInputToKey = (raw: string): VariantKey | null => {
    const parsed = parseVariantInput(raw, isTwoDim);
    if (!parsed.size) return null;
    if (isTwoDim) {
      // In two-dim mode we lock the length to the surrounding group (e.g. before 25/30 => 30).
      const locked = insertLockedLength || parsed.length;
      if (!locked) return null;
      return buildVariantKey(parsed.size, locked);
    }
    return buildVariantKey(parsed.size, parsed.length);
  };

  const insertVariantAtPosition = (pos: number, rawInput: string) => {
    const normalizedKey = normalizeInsertInputToKey(rawInput);
    if (!normalizedKey) return;
    if (variantKeys.includes(normalizedKey)) return;
    const newKeys = [
      ...variantKeys.slice(0, pos),
      normalizedKey,
      ...variantKeys.slice(pos),
    ];
    // Rebuild skus in color-major order so insertion-order sizing is preserved
    const nextSkus: PurchaseLineSku[] = [];
    for (const colorCode of colorCodes) {
      for (const key of newKeys) {
        if (key === normalizedKey) {
          const { size, length } = parseVariantKey(key);
          const base: PurchaseLineSku = {
            ...createEmptySku(line.articleNumber, line.productName),
            attribute1Code: colorCode,
            attribute2Code: size,
            attribute3Code: length,
            supplierPrice: line.bulkSupplierPrice ?? null,
            priceOrig: line.bulkPriceOrig ?? null,
            price: effectiveBulkPrice,
            weight: productWeight,
          };
          nextSkus.push(hydrateFromSystemSku(base));
        } else {
          const existing = cellSku(colorCode, key);
          if (existing) nextSkus.push(existing);
        }
      }
    }
    onChange({ ...line, skus: nextSkus });
  };

  const convertExistingSkusToLength = (length: string) => {
    const nextSkus = line.skus.map((sku) => {
      const size = (sku.attribute2Code || '').trim();
      if (!size) return sku;
      // only assign to SKUs without length, so we don't clobber any existing two-dim data
      if (sku.attribute3Code) return sku;
      return hydrateFromSystemSku({ ...sku, attribute3Code: length });
    });
    onChange({ ...line, skus: nextSkus });
  };

  const addLengthForColor = (colorCode: string, length: string) => {
    const l = (length || '').trim();
    if (!colorCode || !l) return;

    const sizeSet = new Set<string>();
    for (const key of variantKeys) {
      const { size } = parseVariantKey(key);
      if (size) sizeSet.add(size);
    }
    const baseSizes = Array.from(sizeSet);
    if (baseSizes.length === 0) return;

    const existingKeys = new Set(
      line.skus
        .filter((s) => s.attribute1Code === colorCode)
        .map((s) => buildVariantKey(s.attribute2Code || '', s.attribute3Code)),
    );

    const newSkus: PurchaseLineSku[] = [];
    for (const size of baseSizes) {
      const key = buildVariantKey(size, l);
      if (existingKeys.has(key)) continue;
      const base: PurchaseLineSku = {
        ...createEmptySku(line.articleNumber, line.productName),
        attribute1Code: colorCode,
        attribute2Code: size,
        attribute3Code: l,
        supplierPrice: line.bulkSupplierPrice ?? null,
        priceOrig: line.bulkPriceOrig ?? null,
        price: effectiveBulkPrice,
        weight: productWeight,
      };
      newSkus.push(hydrateFromSystemSku(base));
    }

    if (newSkus.length === 0) return;
    onChange({ ...line, skus: [...line.skus, ...newSkus] });
  };

  const removeVariantColumn = (variantKey: VariantKey) => {
    const { size, length } = parseVariantKey(variantKey);
    onChange({
      ...line,
      skus: line.skus.filter(
        (s) =>
          !(
            (s.attribute2Code || '') === size &&
            (length ? (s.attribute3Code || '') === length : !s.attribute3Code)
          ),
      ),
    });
    setRemoveSizeCode(null);
  };

  const removeColor = (code: string) => {
    onChange({
      ...line,
      skus: line.skus.filter((s) => s.attribute1Code !== code),
    });
  };

  const openEditColor = (colorCode: string) => {
    setEditColorCode(colorCode);
    setEditColorValue(colorCode);
    setEditColorError(null);
  };

  const applyColorRename = () => {
    const fromCode = (editColorCode || '').trim();
    const toCode = editColorValue.trim();
    if (!fromCode) return;
    if (!toCode) {
      setEditColorError('Ange ett färgnamn.');
      return;
    }
    if (isColorInSystem(fromCode)) {
      setEditColorError(
        'Färger som redan finns i systemet kan inte ändras här.',
      );
      return;
    }

    const duplicate = colorCodes.some(
      (code) =>
        code !== fromCode &&
        normalizeAttributeLookupKey(code) ===
          normalizeAttributeLookupKey(toCode),
    );
    if (duplicate) {
      setEditColorError('Det finns redan en färgrad med det namnet.');
      return;
    }

    const nextManualPriceColors = Array.from(
      new Set(
        (line.manualPriceColors || []).map((code) =>
          code === fromCode ? toCode : code,
        ),
      ),
    );

    onChange({
      ...line,
      manualPriceColors: nextManualPriceColors,
      skus: line.skus.map((sku) =>
        sku.attribute1Code === fromCode
          ? hydrateFromSystemSku({ ...sku, attribute1Code: toCode })
          : sku,
      ),
    });

    if (dataDialogColor === fromCode) setDataDialogColor(toCode);
    if (createColorCode === fromCode) setCreateColorCode(toCode);
    setEditColorCode(null);
    setEditColorValue('');
    setEditColorError(null);
  };

  const fillRow = (code: string, value: number) => {
    onChange({
      ...line,
      skus: line.skus.map((s) =>
        s.attribute1Code === code ? { ...s, quantity: value } : s,
      ),
    });
  };

  const copyRow = (toCode: string, fromCode: string) => {
    const qtys = new Map(
      line.skus
        .filter((s) => s.attribute1Code === fromCode)
        .map((s) => [
          buildVariantKey(s.attribute2Code || '', s.attribute3Code),
          s.quantity || 0,
        ]),
    );
    onChange({
      ...line,
      skus: line.skus.map((s) => {
        if (s.attribute1Code !== toCode) return s;
        const q = qtys.get(
          buildVariantKey(s.attribute2Code || '', s.attribute3Code),
        );
        return q !== undefined ? { ...s, quantity: q } : s;
      }),
    });
  };

  const applyCurve = (code: string, pattern: string) => {
    const values = pattern
      .split(',')
      .map((p) => Number(p.trim()))
      .filter((n) => Number.isFinite(n) && n >= 0);

    const rowSkus = line.skus
      .map((sku, idx) => ({ sku, idx }))
      .filter(({ sku }) => sku.attribute1Code === code && sku.attribute2Code)
      .sort((a, b) => {
        const aKey = buildVariantKey(
          a.sku.attribute2Code || '',
          a.sku.attribute3Code,
        );
        const bKey = buildVariantKey(
          b.sku.attribute2Code || '',
          b.sku.attribute3Code,
        );
        const ai = variantKeys.findIndex((k) => k === aKey);
        const bi = variantKeys.findIndex((k) => k === bKey);
        // Fall back to attribute2 ordering if not found (shouldn't happen)
        if (ai !== -1 && bi !== -1) return ai - bi;
        const as = attribute2Options.findIndex(
          (o) => o.code === a.sku.attribute2Code,
        );
        const bs = attribute2Options.findIndex(
          (o) => o.code === b.sku.attribute2Code,
        );
        return as - bs;
      });

    const nextSkus = [...line.skus];
    rowSkus.forEach(({ idx }, pos) => {
      nextSkus[idx] = { ...nextSkus[idx], quantity: values[pos] ?? 0 };
    });
    onChange({ ...line, skus: nextSkus });
  };

  const setColorRowMonth = (colorCode: string, month: string | null) => {
    onChange({
      ...line,
      skus: line.skus.map((s) =>
        s.attribute1Code === colorCode ? { ...s, deliveryMonth: month } : s,
      ),
    });
  };

  const colorRowMonth = (colorCode: string): string | null => {
    const rowSkus = line.skus.filter((s) => s.attribute1Code === colorCode);
    if (rowSkus.length === 0) return null;
    const first = rowSkus[0].deliveryMonth;
    return rowSkus.every((s) => s.deliveryMonth === first)
      ? (first ?? null)
      : null;
  };

  const curvePresets = useMemo(() => buildPresets(variantKeys), [variantKeys]);

  const colorLabel = (code: string) =>
    attribute1Options.find((o) => o.code === code)?.code || code;

  const addColorCode = colorCodeFromAutocompleteValue(addColorValue);
  const addColorIsMissing =
    Boolean(addColorCode) && !isColorInSystem(addColorCode);

  useEffect(() => {
    if (!createColorCode) return;

    let cancelled = false;
    setCreateColorNumber('');
    setCreateColorError(null);
    setCreateColorLoadingNumber(true);

    fetchNextAttributeNumber()
      .then((nextNumber) => {
        if (!cancelled) setCreateColorNumber(nextNumber);
      })
      .catch((error) => {
        console.error('Failed to load next color number:', error);
        if (!cancelled) {
          setCreateColorError('Kunde inte hämta nästa färgnummer.');
        }
      })
      .finally(() => {
        if (!cancelled) setCreateColorLoadingNumber(false);
      });

    return () => {
      cancelled = true;
    };
  }, [createColorCode]);

  const handleCreateColor = async () => {
    const code = (createColorCode || '').trim();
    const number = createColorNumber.trim();
    if (!code || !number) return;

    try {
      setCreateColorSaving(true);
      setCreateColorError(null);
      const created = await createAttributeValue({
        attributeType: 'attribute1',
        attributeCode: code,
        attributeNumber: number,
      });
      await onAttribute1Created?.(created);
      setCreateColorCode(null);
      setCreateColorNumber('');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || null;
      setCreateColorError(message || 'Kunde inte lägga upp färgen.');
    } finally {
      setCreateColorSaving(false);
    }
  };

  const manualPriceColorSet = useMemo(
    () => new Set(line.manualPriceColors || []),
    [line.manualPriceColors],
  );

  const setColorManualPriceState = (colorCode: string, isManual: boolean) => {
    const next = new Set(line.manualPriceColors || []);
    if (isManual) {
      next.add(colorCode);
    } else {
      next.delete(colorCode);
    }
    return Array.from(next);
  };

  const updateColorPrices = (
    colorCode: string,
    next: Partial<
      Pick<PurchaseLineSku, 'supplierPrice' | 'priceOrig' | 'price'>
    >,
  ) => {
    onChange({
      ...line,
      manualPriceColors: setColorManualPriceState(colorCode, true),
      skus: line.skus.map((sku) =>
        sku.attribute1Code === colorCode ? { ...sku, ...next } : sku,
      ),
    });
  };

  const resetColorPricesToBulk = (colorCode: string) => {
    onChange({
      ...line,
      manualPriceColors: setColorManualPriceState(colorCode, false),
      skus: line.skus.map((sku) =>
        sku.attribute1Code === colorCode
          ? {
              ...sku,
              supplierPrice:
                line.bulkSupplierPrice ?? sku.supplierPrice ?? null,
              priceOrig: line.bulkPriceOrig ?? sku.priceOrig ?? null,
              price: effectiveBulkPrice ?? sku.price ?? null,
            }
          : sku,
      ),
    });
  };

  const updateDataSku = (
    globalIdx: number,
    next: Partial<PurchaseLineSku>,
    options?: { markManualPrice?: boolean },
  ) => {
    const colorCode = line.skus[globalIdx]?.attribute1Code || '';
    const nextSkus = line.skus.map((s, i) =>
      i === globalIdx ? { ...s, ...next } : s,
    );
    onChange({
      ...line,
      skus: nextSkus,
      manualPriceColors:
        options?.markManualPrice && colorCode
          ? setColorManualPriceState(colorCode, true)
          : line.manualPriceColors,
    });
  };

  const dataSkus = dataDialogColor
    ? line.skus
        .map((sku, idx) => ({ sku, idx }))
        .filter(({ sku }) => sku.attribute1Code === dataDialogColor)
        .sort((a, b) => {
          const aKey = buildVariantKey(
            a.sku.attribute2Code || '',
            a.sku.attribute3Code,
          );
          const bKey = buildVariantKey(
            b.sku.attribute2Code || '',
            b.sku.attribute3Code,
          );
          const ai = variantKeys.findIndex((k) => k === aKey);
          const bi = variantKeys.findIndex((k) => k === bKey);
          return ai - bi;
        })
    : [];
  const dataColorSample = dataSkus[0]?.sku;
  const detectedDataColorCampaign = Boolean(
    dataSkus.some(
      ({ sku }) =>
        sku.price != null &&
        sku.priceOrig != null &&
        Number(sku.price) !== Number(sku.priceOrig),
    ),
  );

  const updateDataColorPrices = (
    colorCode: string,
    next: Partial<
      Pick<PurchaseLineSku, 'supplierPrice' | 'priceOrig' | 'price'>
    >,
  ) => {
    updateColorPrices(colorCode, next);
  };

  const setDataColorCampaign = (colorCode: string, enabled: boolean) => {
    const colorSkus = line.skus.filter(
      (sku) => sku.attribute1Code === colorCode,
    );
    const fallbackPriceOrig =
      colorSkus[0]?.priceOrig ?? line.bulkPriceOrig ?? line.bulkPrice ?? null;
    const existingCampaignPrice = colorSkus[0]?.price ?? fallbackPriceOrig;
    updateColorPrices(colorCode, {
      price: enabled ? existingCampaignPrice : fallbackPriceOrig,
    });
  };

  useEffect(() => {
    if (!dataDialogColor) return;
    setDataColorCampaignEnabled(detectedDataColorCampaign);
  }, [dataDialogColor, detectedDataColorCampaign]);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ minWidth: 160, fontWeight: 600 }}>
                Färg
              </TableCell>
              {variantKeys.map((key, sizeIdx) => (
                <React.Fragment key={key}>
                  {/* Ghost insert-before cell */}
                  <TableCell
                    sx={{
                      width: 14,
                      minWidth: 14,
                      maxWidth: 14,
                      p: 0,
                      border: 'none',
                      '&:hover .sz-insert-btn': { opacity: 1 },
                    }}
                  >
                    {!readOnly && (
                      <Box
                        className="sz-insert-btn"
                        onClick={(e) => {
                          openInsertPopover(
                            e.currentTarget as HTMLElement,
                            sizeIdx,
                          );
                        }}
                        sx={{
                          opacity: 0,
                          transition: 'opacity 0.15s',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 36,
                        }}
                      >
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1,
                            boxShadow: 1,
                          }}
                        >
                          +
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      minWidth: 52,
                      px: 0.5,
                      position: 'relative',
                      '&:hover .sz-remove-btn': readOnly ? {} : { opacity: 1 },
                    }}
                  >
                    {formatVariantLabel(key)}
                    {!readOnly && (
                      <Box
                        className="sz-remove-btn"
                        onClick={() => setRemoveSizeCode(key)}
                        sx={{
                          opacity: 0,
                          transition: 'opacity 0.15s',
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 14,
                          height: 14,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          bgcolor: 'error.main',
                          color: 'white',
                          fontSize: 11,
                          fontWeight: 700,
                          lineHeight: 1,
                          boxShadow: 1,
                        }}
                      >
                        ×
                      </Box>
                    )}
                  </TableCell>
                </React.Fragment>
              ))}
              {/* Ghost insert-after-last cell */}
              {variantKeys.length > 0 && !readOnly && (
                <TableCell
                  sx={{
                    width: 14,
                    minWidth: 14,
                    maxWidth: 14,
                    p: 0,
                    border: 'none',
                    '&:hover .sz-insert-btn': { opacity: 1 },
                  }}
                >
                  <Box
                    className="sz-insert-btn"
                    onClick={(e) => {
                      openInsertPopover(
                        e.currentTarget as HTMLElement,
                        variantKeys.length,
                      );
                    }}
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.15s',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 36,
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: 1,
                        boxShadow: 1,
                      }}
                    >
                      +
                    </Box>
                  </Box>
                </TableCell>
              )}
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 56 }}>
                Tot
              </TableCell>
              <TableCell sx={{ width: 80 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {colorCodes.length === 0 && (
              <TableRow>
                <TableCell colSpan={100} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Inga färger tillagda. Klicka "+ Lägg till färg" nedan.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {colorCodes.map((colorCode, colorIdx) => {
              const rowHasMissing = line.skus
                .filter((s) => s.attribute1Code === colorCode)
                .some((s) => hasMissingSkuProductData(s));
              const hasManualPrice = manualPriceColorSet.has(colorCode);
              const colorMissingInSystem = !isColorInSystem(colorCode);

              return (
                <TableRow
                  key={colorCode}
                  sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                >
                  {/* Color label */}
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: 'grey.400',
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {colorLabel(colorCode)}
                        </Typography>
                        {hasManualPrice && (
                          <Chip
                            size="small"
                            label="Manuellt pris"
                            color="info"
                            variant="outlined"
                            sx={{ mt: 0.4, height: 17, fontSize: 10 }}
                          />
                        )}
                        {colorMissingInSystem && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.4 }}>
                            <Chip
                              size="small"
                              label="Ej upplagd i systemet"
                              color="warning"
                              variant="outlined"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                cursor: 'pointer',
                              }}
                              onClick={() => setCreateColorCode(colorCode)}
                            />
                            {!readOnly && (
                              <Chip
                                size="small"
                                label="Ändra"
                                icon={<EditOutlinedIcon />}
                                variant="outlined"
                                sx={{
                                  height: 18,
                                  fontSize: 10,
                                  cursor: 'pointer',
                                  '& .MuiChip-icon': { fontSize: 13 },
                                }}
                                onClick={() => openEditColor(colorCode)}
                              />
                            )}
                          </Stack>
                        )}
                        {deliveryMonths.length > 0 && (
                          <TextField
                            select
                            variant="standard"
                            size="small"
                            value={colorRowMonth(colorCode) || '__inherit__'}
                            disabled={readOnly}
                            onChange={(e) =>
                              setColorRowMonth(
                                colorCode,
                                e.target.value === '__inherit__'
                                  ? null
                                  : e.target.value,
                              )
                            }
                            SelectProps={{ disableUnderline: true }}
                            sx={{
                              '& .MuiSelect-select': {
                                fontSize: 10,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                color: colorRowMonth(colorCode)
                                  ? 'warning.main'
                                  : 'text.disabled',
                                py: 0,
                                pr: '14px !important',
                              },
                              '& .MuiSvgIcon-root': { fontSize: 12 },
                            }}
                          >
                            <MenuItem value="__inherit__" sx={{ fontSize: 12 }}>
                              Använd produktens månad
                            </MenuItem>
                            {deliveryMonths.map((m) => (
                              <MenuItem
                                key={m.value}
                                value={m.value}
                                sx={{ fontSize: 12 }}
                              >
                                {m.short || m.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      </Box>
                      {rowHasMissing && (
                        <Chip
                          size="small"
                          color="warning"
                          label="Saknar data"
                          sx={{ height: 18, fontSize: 10, cursor: 'pointer' }}
                          onClick={() => setDataDialogColor(colorCode)}
                        />
                      )}
                    </Stack>
                  </TableCell>

                  {/* Quantity cells with ghost insert spacers */}
                  {variantKeys.map((key, sizeIdx) => {
                    const sku = cellSku(colorCode, key);
                    const qty = sku?.quantity || 0;
                    return (
                      <React.Fragment key={key}>
                        {/* Ghost spacer matching the header insert cell */}
                        <TableCell
                          sx={{
                            width: 14,
                            minWidth: 14,
                            maxWidth: 14,
                            p: 0,
                            border: 'none',
                          }}
                        />
                        <TableCell align="center" sx={{ p: 0.5 }}>
                          <input
                            ref={(el) => {
                              cellRefs.current[`${colorCode}-${key}`] = el;
                            }}
                            type="number"
                            min={0}
                            value={qty || ''}
                            disabled={readOnly || !sku}
                            placeholder="–"
                            onKeyDown={(e) =>
                              handleCellKey(e, colorIdx, sizeIdx)
                            }
                            onChange={(e) =>
                              updateCell(
                                colorCode,
                                key,
                                parseInt(e.target.value) || 0,
                              )
                            }
                            style={{
                              width: 44,
                              height: 30,
                              border: '1px solid transparent',
                              borderRadius: 4,
                              textAlign: 'center',
                              fontSize: 13,
                              fontWeight: qty > 0 ? 600 : 400,
                              color: qty > 0 ? 'inherit' : '#bbb',
                              background:
                                qty > 0
                                  ? 'rgba(63,81,181,0.06)'
                                  : 'transparent',
                              outline: 'none',
                              cursor: readOnly || !sku ? 'default' : 'text',
                              transition:
                                'border-color 0.12s, background 0.12s',
                            }}
                            onFocus={(e) => {
                              e.target.select();
                              e.target.style.borderColor = '#3F51B5';
                              e.target.style.background = '#fff';
                              e.target.style.boxShadow =
                                '0 0 0 2px rgba(63,81,181,0.18)';
                            }}
                            onBlur={(e) => {
                              const v = parseInt(e.target.value) || 0;
                              e.target.style.borderColor = 'transparent';
                              e.target.style.background =
                                v > 0 ? 'rgba(63,81,181,0.06)' : 'transparent';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  {/* Trailing ghost spacer matching the header's append cell */}
                  {variantKeys.length > 0 && !readOnly && (
                    <TableCell
                      sx={{
                        width: 14,
                        minWidth: 14,
                        maxWidth: 14,
                        p: 0,
                        border: 'none',
                      }}
                    />
                  )}

                  {/* Row total */}
                  <TableCell align="right" sx={{ fontWeight: 700, pr: 1.5 }}>
                    {rowTotal(colorCode) > 0 ? (
                      rowTotal(colorCode)
                    ) : (
                      <span style={{ color: '#ccc' }}>0</span>
                    )}
                  </TableCell>

                  {/* Row actions */}
                  <TableCell>
                    <Stack direction="row" spacing={0}>
                      <Tooltip
                        title={
                          rowHasMissing
                            ? 'Produktdata — saknar fält'
                            : 'Produktdata'
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => setDataDialogColor(colorCode)}
                          color={rowHasMissing ? 'warning' : 'default'}
                        >
                          <EditNoteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        disabled={readOnly}
                        onClick={(e) =>
                          setRowMenu({ el: e.currentTarget, colorCode })
                        }
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>

          {/* Footer totals */}
          {variantKeys.length > 0 && colorCodes.length > 0 && (
            <TableFooter>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'text.secondary',
                  }}
                >
                  Per storlek
                </TableCell>
                {variantKeys.map((key) => {
                  const t = colTotal(key);
                  return (
                    <React.Fragment key={key}>
                      <TableCell
                        sx={{
                          width: 14,
                          minWidth: 14,
                          maxWidth: 14,
                          p: 0,
                          border: 'none',
                        }}
                      />
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: t > 0 ? 700 : 400,
                          color: t > 0 ? 'text.primary' : 'text.disabled',
                        }}
                      >
                        {t > 0 ? t : '–'}
                      </TableCell>
                    </React.Fragment>
                  );
                })}
                {!readOnly && (
                  <TableCell
                    sx={{
                      width: 14,
                      minWidth: 14,
                      maxWidth: 14,
                      p: 0,
                      border: 'none',
                    }}
                  />
                )}
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  {grandTotal}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Box>

      {/* Bottom bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="body2" color="text.secondary">
          {grandTotal > 0 ? `${grandTotal} st totalt` : 'Inga antal ifyllda'}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddColorOpen(true)}
          disabled={readOnly}
          variant="outlined"
        >
          Lägg till färg
        </Button>
      </Stack>

      {/* ── Row context menu ────────────────────────────── */}
      <Menu
        anchorEl={rowMenu?.el}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem
          dense
          onClick={() => {
            if (rowMenu) {
              setFillColor(rowMenu.colorCode);
              setFillValue('');
            }
            setRowMenu(null);
          }}
        >
          <ListItemIcon>
            <AutoFixHighIcon fontSize="small" />
          </ListItemIcon>
          Fyll alla storlekar…
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            if (rowMenu) setCurveColor(rowMenu.colorCode);
            setRowMenu(null);
          }}
        >
          <ListItemIcon>
            <AutoFixHighIcon fontSize="small" />
          </ListItemIcon>
          Storlekskurva…
        </MenuItem>
        {isTwoDim && attribute3Options.length > 0 && (
          <MenuItem
            dense
            disabled={readOnly}
            onClick={() => {
              if (rowMenu) {
                setAddLengthColor(rowMenu.colorCode);
                setAddLengthValue('');
              }
              setRowMenu(null);
            }}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            Lägg till längd…
          </MenuItem>
        )}
        {rowMenu && !isColorInSystem(rowMenu.colorCode) && (
          <MenuItem
            dense
            disabled={readOnly}
            onClick={() => {
              if (rowMenu) openEditColor(rowMenu.colorCode);
              setRowMenu(null);
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Ändra färgnamn…
          </MenuItem>
        )}
        {colorCodes
          .filter((c) => c !== rowMenu?.colorCode)
          .map((fromCode) => (
            <MenuItem
              key={fromCode}
              dense
              onClick={() => {
                if (rowMenu) copyRow(rowMenu.colorCode, fromCode);
                setRowMenu(null);
              }}
            >
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              Kopiera från {colorLabel(fromCode)}
            </MenuItem>
          ))}
        <Divider />
        <MenuItem
          dense
          sx={{ color: 'error.main' }}
          onClick={() => {
            if (rowMenu) removeColor(rowMenu.colorCode);
            setRowMenu(null);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          Ta bort färg
        </MenuItem>
      </Menu>

      {/* ── Lägg till längd (för en färg) ───────────────────── */}
      <Dialog
        open={!!addLengthColor}
        onClose={() => {
          setAddLengthColor(null);
          setAddLengthValue('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Lägg till längd</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.5}>
            {systemSkusLoading && isInternalLine && (
              <Alert severity="info">Hämtar SKU-data från systemet…</Alert>
            )}
            <TextField
              select
              size="small"
              label="Längd"
              value={addLengthValue}
              disabled={readOnly}
              onChange={(e) => setAddLengthValue(e.target.value)}
              fullWidth
            >
              <MenuItem value="">–</MenuItem>
              {attribute3Options
                .filter(
                  (opt) =>
                    !String(opt.code || '')
                      .trim()
                      .startsWith('"'),
                )
                .map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>
                    {opt.code}
                  </MenuItem>
                ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              Skapar nya kolumner för vald färg för alla befintliga storlekar.
              Om produkten är systemmatchad fylls PLU/EAN/vikt automatiskt när
              kombinationen finns i systemet.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddLengthColor(null);
              setAddLengthValue('');
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            disabled={!addLengthColor || !addLengthValue.trim()}
            onClick={() => {
              if (addLengthColor && addLengthValue.trim()) {
                addLengthForColor(addLengthColor, addLengthValue.trim());
              }
              setAddLengthColor(null);
              setAddLengthValue('');
            }}
          >
            Lägg till
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Lägg till färg ─────────────────────────────── */}
      <Dialog
        open={addColorOpen}
        onClose={() => {
          setAddColorOpen(false);
          setAddColorTemplateLabel(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lägg till färg</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Autocomplete<AttributeOption, false, false, true>
            freeSolo
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={attribute1Options.filter(
              (o) => !colorCodes.includes(o.code),
            )}
            filterOptions={filterAttributeOptionsBySearchRank}
            value={addColorValue}
            inputValue={addColorCode}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.code
            }
            isOptionEqualToValue={(option, value) =>
              typeof value !== 'string' && option.code === value.code
            }
            onInputChange={(_, value) => setAddColorValue(value)}
            onChange={(_, v) => setAddColorValue(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Färg"
                autoFocus
                helperText={
                  addColorIsMissing
                    ? 'Färgen kan användas på ordern men måste läggas upp innan Viskan-överföring.'
                    : ' '
                }
              />
            )}
          />
          {addColorIsMissing && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              <strong>{addColorCode}</strong> finns inte i systemet än. Ordern
              kan sparas och skapas, men den blir inte redo för Viskan förrän
              färgen är upplagd.
            </Alert>
          )}
          {/* Template picker — only shown for manual lines with no sizes yet */}
          {variantKeys.length === 0 && line.source === 'manual' && (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mb: 1.25,
                  display: 'block',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                }}
              >
                Välj storleksmall
              </Typography>
              <Stack spacing={1}>
                {SIZE_TEMPLATES.map((tmpl) => {
                  const selected = addColorTemplateLabel === tmpl.label;
                  return (
                    <Box
                      key={tmpl.label}
                      onClick={() =>
                        setAddColorTemplateLabel(selected ? null : tmpl.label)
                      }
                      sx={{
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 1,
                        cursor: 'pointer',
                        bgcolor: selected ? 'primary.50' : 'transparent',
                        transition: 'border-color 0.15s, background 0.15s',
                        '&:hover': {
                          borderColor: selected
                            ? 'primary.main'
                            : 'primary.light',
                          bgcolor: selected ? 'primary.50' : 'action.hover',
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={selected ? 'primary.main' : 'text.primary'}
                        sx={{ display: 'block', mb: 0.75 }}
                      >
                        {tmpl.label}
                      </Typography>
                      <Stack spacing={0.75}>
                        {tmpl.lengths && tmpl.lengths.length > 0 ? (
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mr: 0.5 }}
                            >
                              Exempel:
                            </Typography>
                            {tmpl.lengths
                              .flatMap((l) =>
                                tmpl.sizes.map((s) =>
                                  formatVariantLabel(buildVariantKey(s, l)),
                                ),
                              )
                              .slice(0, 18)
                              .map((label) => (
                                <Box
                                  key={label}
                                  sx={{
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 0.75,
                                    bgcolor: selected
                                      ? 'primary.main'
                                      : 'grey.100',
                                    color: selected
                                      ? 'primary.contrastText'
                                      : 'text.secondary',
                                    fontSize: 11,
                                    fontWeight: 500,
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {label}
                                </Box>
                              ))}
                            {tmpl.lengths.length * tmpl.sizes.length > 18 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 0.25 }}
                              >
                                …
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {tmpl.sizes.slice(0, 18).map((s) => (
                              <Box
                                key={s}
                                sx={{
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 0.75,
                                  bgcolor: selected
                                    ? 'primary.main'
                                    : 'grey.100',
                                  color: selected
                                    ? 'primary.contrastText'
                                    : 'text.secondary',
                                  fontSize: 11,
                                  fontWeight: 500,
                                  lineHeight: 1.6,
                                }}
                              >
                                {s}
                              </Box>
                            ))}
                            {tmpl.sizes.length > 18 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 0.25 }}
                              >
                                …
                              </Typography>
                            )}
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  );
                })}

                <Box
                  onClick={() => setCustomTemplateOpen(true)}
                  sx={{
                    border: '2px dashed',
                    borderColor:
                      customSizes.length > 0 ? 'primary.main' : 'divider',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    bgcolor:
                      customSizes.length > 0 ? 'primary.50' : 'transparent',
                    transition: 'border-color 0.15s, background 0.15s',
                    '&:hover': {
                      borderColor:
                        customSizes.length > 0
                          ? 'primary.main'
                          : 'primary.light',
                      bgcolor:
                        customSizes.length > 0 ? 'primary.50' : 'action.hover',
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={
                      customSizes.length > 0 ? 'primary.main' : 'text.primary'
                    }
                    sx={{ display: 'block', mb: 0.75 }}
                  >
                    Bygg egen mall…
                  </Typography>
                  {customSizes.length > 0 ? (
                    <Stack spacing={0.75}>
                      {customUseLengths && customLengths.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mr: 0.5 }}
                          >
                            Längder:
                          </Typography>
                          {customLengths.slice(0, 12).map((l) => (
                            <Box
                              key={l}
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.75,
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                fontSize: 11,
                                fontWeight: 500,
                                lineHeight: 1.6,
                              }}
                            >
                              {l}
                            </Box>
                          ))}
                          {customLengths.length > 12 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              +{customLengths.length - 12}
                            </Typography>
                          )}
                        </Stack>
                      )}
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mr: 0.5 }}
                        >
                          Storlekar:
                        </Typography>
                        {customSizes.slice(0, 14).map((s) => (
                          <Box
                            key={s}
                            sx={{
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.75,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              fontSize: 11,
                              fontWeight: 500,
                              lineHeight: 1.6,
                            }}
                          >
                            {s}
                          </Box>
                        ))}
                        {customSizes.length > 14 && (
                          <Typography variant="caption" color="text.secondary">
                            +{customSizes.length - 14}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Välj storlekar från systemet (och ev. längder)
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddColorOpen(false);
              setAddColorTemplateLabel(null);
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={addColor}
            disabled={
              !addColorCode ||
              (variantKeys.length === 0 &&
                line.source === 'manual' &&
                !addColorTemplateLabel &&
                customSizes.length === 0)
            }
          >
            Lägg till
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Ändra ej upplagd färg ─────────────────────────── */}
      <Dialog
        open={Boolean(editColorCode)}
        onClose={() => {
          setEditColorCode(null);
          setEditColorValue('');
          setEditColorError(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Ändra färgnamn</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2}>
            <Alert severity="info">
              Ändringen uppdaterar färgraden på inköpsordern. Färgen läggs inte
              upp i systemet förrän du väljer att lägga upp den.
            </Alert>
            <TextField
              label="Färgnamn"
              value={editColorValue}
              onChange={(event) => {
                setEditColorValue(event.target.value);
                setEditColorError(null);
              }}
              fullWidth
              autoFocus
              required
              helperText={
                editColorValue.trim() && isColorInSystem(editColorValue)
                  ? 'Matchar en färg som redan finns i systemet.'
                  : ' '
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyColorRename();
                }
              }}
            />
            {editColorError && <Alert severity="error">{editColorError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditColorCode(null);
              setEditColorValue('');
              setEditColorError(null);
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={applyColorRename}
            disabled={!editColorValue.trim()}
          >
            Spara ändring
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Lägg upp saknad färg i systemet ───────────────── */}
      <Dialog
        open={Boolean(createColorCode)}
        onClose={() => {
          if (createColorSaving) return;
          setCreateColorCode(null);
          setCreateColorNumber('');
          setCreateColorError(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Lägg upp färg</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2}>
            <Alert severity="info">
              Färgen läggs till i systemets färgregister. När den finns där kan
              ordern bli redo för Viskan.
            </Alert>
            <TextField
              label="Färgkod"
              value={createColorCode || ''}
              fullWidth
              disabled
            />
            <TextField
              label="Färgnummer"
              value={createColorNumber}
              onChange={(event) => setCreateColorNumber(event.target.value)}
              fullWidth
              required
              disabled={createColorLoadingNumber || createColorSaving}
              helperText="Nästa lediga färgnummer föreslås automatiskt."
            />
            {createColorError && (
              <Alert severity="error">{createColorError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateColorCode(null);
              setCreateColorNumber('');
              setCreateColorError(null);
            }}
            disabled={createColorSaving}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateColor}
            disabled={
              createColorSaving ||
              createColorLoadingNumber ||
              !createColorCode?.trim() ||
              !createColorNumber.trim()
            }
          >
            {createColorSaving ? 'Lägger upp…' : 'Lägg upp färg'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bygg egen storleksmall ─────────────────────── */}
      <Dialog
        open={customTemplateOpen}
        onClose={() => setCustomTemplateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bygg egen storleksmall</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Välj de storlekar som finns i systemet. Vanliga storlekar visas
              först.
            </Typography>

            <Autocomplete
              multiple
              options={systemSizeCodes}
              value={customSizes}
              disableCloseOnSelect
              onChange={(_, v) => {
                setCustomSizes(v);
                if (v.length > 0) setAddColorTemplateLabel(null);
              }}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Storlekar"
                  placeholder="Sök storlek…"
                />
              )}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={customUseLengths}
                  onChange={(_, checked) => {
                    setCustomUseLengths(checked);
                    if (!checked) setCustomLengths([]);
                  }}
                />
              }
              label="Använd längd"
            />

            {customUseLengths && (
              <Autocomplete
                multiple
                options={systemLengthCodes}
                value={customLengths}
                disableCloseOnSelect
                onChange={(_, v) => setCustomLengths(v)}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Längder"
                    placeholder={
                      systemLengthCodes.length > 0
                        ? 'Sök längd…'
                        : 'Inga längder tillgängliga'
                    }
                  />
                )}
                disabled={systemLengthCodes.length === 0}
              />
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                onClick={() => {
                  setCustomSizes([]);
                  setCustomUseLengths(false);
                  setCustomLengths([]);
                }}
              >
                Rensa
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomTemplateOpen(false)}>Stäng</Button>
          <Button
            variant="contained"
            onClick={() => setCustomTemplateOpen(false)}
            disabled={
              customSizes.length === 0 ||
              (customUseLengths &&
                systemLengthCodes.length > 0 &&
                customLengths.length === 0)
            }
          >
            Använd
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Fyll alla storlekar ────────────────────────── */}
      <Dialog
        open={!!fillColor}
        onClose={() => setFillColor(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Fyll alla storlekar</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <TextField
            label="Antal per storlek"
            type="number"
            value={fillValue}
            onChange={(e) => setFillValue(e.target.value)}
            autoFocus
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFillColor(null)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (fillColor) fillRow(fillColor, parseInt(fillValue) || 0);
              setFillColor(null);
            }}
          >
            Applicera
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Storlekskurva ─────────────────────────────── */}
      <Dialog
        open={!!curveColor}
        onClose={() => setCurveColor(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Storlekskurva</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Ange antal per storlek, kommaseparerat i storleksordning. Exempel:{' '}
              <strong>1,2,3,3,2,1</strong>
            </Typography>
            {curvePresets.length > 0 && (
              <Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Snabbval
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {curvePresets.map((preset) => (
                    <Chip
                      key={preset.label}
                      label={preset.label}
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={() => setCurvePattern(preset.values.join(','))}
                    />
                  ))}
                </Stack>
              </Stack>
            )}
            <TextField
              label="Kurva"
              value={curvePattern}
              onChange={(e) => setCurvePattern(e.target.value)}
              autoFocus
              placeholder="t.ex. 1,2,3,3,2,1"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCurveColor(null)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (curveColor) applyCurve(curveColor, curvePattern);
              setCurveColor(null);
            }}
          >
            Applicera
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Infoga storlek ────────────────────────────── */}
      <Popover
        open={Boolean(insertAnchorEl)}
        anchorEl={insertAnchorEl}
        onClose={() => {
          setInsertAnchorEl(null);
          setInsertSizeInput('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, width: 260 } } }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1.5, display: 'block' }}
        >
          Infoga storlek{' '}
          {insertAtPosition !== null && insertAtPosition < variantKeys.length
            ? `före ${formatVariantLabel(variantKeys[insertAtPosition])}`
            : 'sist'}
        </Typography>
        {isTwoDim && (
          <TextField
            size="small"
            fullWidth
            label="Längd"
            value={insertLockedLength || ''}
            disabled
            sx={{ mb: 1.25 }}
          />
        )}
        <TextField
          key={insertPopoverKey}
          size="small"
          fullWidth
          autoFocus
          label={isTwoDim ? 'Storlek' : undefined}
          placeholder="Sök eller skriv storlek"
          value={insertSizeInput}
          onChange={(e) => setInsertSizeInput(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              insertSizeInput.trim() &&
              !variantKeys.includes(
                normalizeInsertInputToKey(insertSizeInput.trim()) || '',
              )
            ) {
              if (insertAtPosition !== null) {
                insertVariantAtPosition(
                  insertAtPosition,
                  insertSizeInput.trim(),
                );
              }
              setInsertAnchorEl(null);
              setInsertSizeInput('');
            }
          }}
        />
        {/* Filtered options list — computed inline to guarantee fresh insertSizeInput */}
        {(() => {
          const allCodes =
            attribute2Options.length > 0
              ? attribute2Options.map((o) => o.code)
              : ALL_KNOWN_SIZES;
          // deduplicate + exclude already-used sizes
          const seen = new Set<string>();
          const base: string[] = [];
          for (const s of allCodes) {
            const key = isTwoDim
              ? buildVariantKey(s, insertLockedLength)
              : buildVariantKey(s);
            if (!seen.has(s) && !variantKeys.includes(key)) {
              seen.add(s);
              base.push(s);
            }
          }
          const q = insertSizeInput.trim().toLowerCase();
          const filtered = q
            ? base.filter((s) => s.toLowerCase().includes(q))
            : base;
          if (filtered.length === 0) return null;
          return (
            <Box
              sx={{
                mt: 0.5,
                maxHeight: 200,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {filtered.slice(0, 50).map((s) => (
                <Box
                  key={s}
                  onMouseDown={(e) => {
                    // onMouseDown so the TextField doesn't lose focus first
                    e.preventDefault();
                    if (insertAtPosition !== null) {
                      insertVariantAtPosition(insertAtPosition, s);
                    }
                    setInsertAnchorEl(null);
                    setInsertSizeInput('');
                  }}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    fontSize: 13,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:not(:last-child)': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  {s}
                </Box>
              ))}
            </Box>
          );
        })()}
        {!isTwoDim && attribute3Options.length > 0 && (
          <Box
            sx={{
              mt: 1.25,
              pt: 1.25,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}
            >
              Behöver du längd? Konvertera matrisen (gäller befintliga rutor).
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                select
                size="small"
                label="Längd"
                value={convertLength}
                onChange={(e) => setConvertLength(e.target.value)}
                sx={{ flex: 1 }}
              >
                <MenuItem value="">–</MenuItem>
                {attribute3Options
                  .filter(
                    (opt) =>
                      !String(opt.code || '')
                        .trim()
                        .startsWith('"'),
                  )
                  .map((opt) => (
                    <MenuItem key={opt.code} value={opt.code}>
                      {opt.code}
                    </MenuItem>
                  ))}
              </TextField>
              <Button
                size="small"
                variant="outlined"
                disabled={readOnly || !convertLength.trim()}
                onClick={() => {
                  const l = convertLength.trim();
                  if (!l) return;
                  convertExistingSkusToLength(l);
                  setConvertLength('');
                  setInsertAnchorEl(null);
                  setInsertSizeInput('');
                }}
              >
                Konvertera
              </Button>
            </Stack>
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: 1.5 }}
        >
          <Button
            size="small"
            onClick={() => {
              setInsertAnchorEl(null);
              setInsertSizeInput('');
            }}
          >
            Avbryt
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!normalizeInsertInputToKey(insertSizeInput.trim())}
            onClick={() => {
              if (insertAtPosition !== null && insertSizeInput.trim()) {
                insertVariantAtPosition(
                  insertAtPosition,
                  insertSizeInput.trim(),
                );
              }
              setInsertAnchorEl(null);
              setInsertSizeInput('');
            }}
          >
            Lägg till
          </Button>
        </Stack>
      </Popover>

      {/* ── Ta bort storlek ──────────────────────────── */}
      {(() => {
        const sizeTotal = removeSizeCode
          ? line.skus
              .filter((s) => {
                const { size, length } = parseVariantKey(removeSizeCode);
                return (
                  (s.attribute2Code || '') === size &&
                  (length
                    ? (s.attribute3Code || '') === length
                    : !s.attribute3Code)
                );
              })
              .reduce((sum, s) => sum + (s.quantity || 0), 0)
          : 0;
        return (
          <Dialog
            open={!!removeSizeCode}
            onClose={() => setRemoveSizeCode(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Ta bort storlek</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                <Typography>
                  Vill du ta bort storleken{' '}
                  <strong>
                    {removeSizeCode ? formatVariantLabel(removeSizeCode) : ''}
                  </strong>{' '}
                  från matrixen?
                </Typography>
                {sizeTotal > 0 && (
                  <Alert severity="warning">
                    Det finns <strong>{sizeTotal} st</strong> inlagda på denna
                    storlek — de raderas med.
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRemoveSizeCode(null)}>Avbryt</Button>
              <Button
                variant="contained"
                color="error"
                onClick={() =>
                  removeSizeCode && removeVariantColumn(removeSizeCode)
                }
              >
                Ta bort
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* ── Produktdata per färg-rad ───────────────────── */}
      <Dialog
        open={!!dataDialogColor}
        onClose={() => setDataDialogColor(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Produktdata — {dataDialogColor ? colorLabel(dataDialogColor) : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {dataDialogColor && (
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
              >
                <Stack spacing={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    Färgpris (gäller alla storlekar i färgen)
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      label="Inpris"
                      value={dataColorSample?.supplierPrice ?? ''}
                      disabled={readOnly}
                      onChange={(e) =>
                        updateDataColorPrices(dataDialogColor, {
                          supplierPrice: numberValue(e.target.value),
                        })
                      }
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Ordinarie pris"
                      value={dataColorSample?.priceOrig ?? ''}
                      disabled={readOnly}
                      onChange={(e) => {
                        const nextValue = numberValue(e.target.value);
                        updateDataColorPrices(dataDialogColor, {
                          priceOrig: nextValue,
                          price: dataColorCampaignEnabled
                            ? (dataColorSample?.price ?? nextValue)
                            : nextValue,
                        });
                      }}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Kampanjpris"
                      value={
                        dataColorCampaignEnabled
                          ? (dataColorSample?.price ?? '')
                          : ''
                      }
                      disabled={readOnly || !dataColorCampaignEnabled}
                      onChange={(e) =>
                        updateDataColorPrices(dataDialogColor, {
                          price: numberValue(e.target.value),
                        })
                      }
                      fullWidth
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      variant={
                        dataColorCampaignEnabled ? 'contained' : 'outlined'
                      }
                      disabled={readOnly}
                      onClick={() => {
                        const nextEnabled = !dataColorCampaignEnabled;
                        setDataColorCampaignEnabled(nextEnabled);
                        setDataColorCampaign(dataDialogColor, nextEnabled);
                      }}
                    >
                      {dataColorCampaignEnabled
                        ? 'Ta bort kampanj'
                        : 'Sätt kampanj'}
                    </Button>
                    {manualPriceColorSet.has(dataDialogColor) && (
                      <Button
                        size="small"
                        disabled={readOnly}
                        onClick={() => resetColorPricesToBulk(dataDialogColor)}
                      >
                        Återställ mot huvudpris
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Box>
            )}
            {dataSkus.map(({ sku, idx: globalIdx }, pos) => {
              const missing = hasMissingSkuProductData(sku);
              const eanValue = sku.ean13 || '';
              const eanKey = skuLockKey(sku, globalIdx);
              const hasEanValue = Boolean(eanValue.trim());
              const eanLocked = hasEanValue && !unlockedEanKeys.has(eanKey);
              return (
                <Box key={globalIdx}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Storlek: {sku.attribute2Code || '?'}
                    </Typography>
                    {sku.attribute3Code && (
                      <Typography variant="body2" color="text.secondary">
                        Längd: {sku.attribute3Code}
                      </Typography>
                    )}
                    {missing && (
                      <Chip size="small" color="warning" label="Saknar data" />
                    )}
                  </Stack>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        size="small"
                        label="PLU"
                        value={sku.pluNo || ''}
                        disabled={readOnly || Boolean(sku.sourceSkuId)}
                        onChange={(e) =>
                          updateDataSku(globalIdx, { pluNo: e.target.value })
                        }
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="EAN13"
                        value={eanValue}
                        disabled={readOnly}
                        sx={
                          eanLocked
                            ? {
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'grey.100',
                                  '& fieldset': {
                                    borderColor: 'grey.500',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'grey.600',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'grey.600',
                                    borderWidth: 1,
                                  },
                                },
                                '& .MuiInputBase-input': {
                                  cursor: 'default',
                                  fontWeight: 600,
                                },
                              }
                            : undefined
                        }
                        inputRef={(element) => {
                          eanInputRefs.current[eanKey] = element;
                        }}
                        inputProps={{
                          maxLength: 13,
                          readOnly: eanLocked,
                        }}
                        InputProps={{
                          endAdornment: hasEanValue ? (
                            <InputAdornment position="end">
                              <Tooltip
                                title={
                                  eanLocked
                                    ? 'Lås upp EAN för ändring'
                                    : 'Lås EAN igen'
                                }
                              >
                                <span>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    disabled={readOnly}
                                    aria-label={
                                      eanLocked
                                        ? 'Lås upp EAN för ändring'
                                        : 'Lås EAN igen'
                                    }
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                    }}
                                    onClick={() => {
                                      if (eanLocked) {
                                        unlockEanField(eanKey);
                                      } else {
                                        lockEanField(eanKey);
                                      }
                                    }}
                                  >
                                    <KeyIcon
                                      fontSize="small"
                                      color={eanLocked ? 'action' : 'primary'}
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </InputAdornment>
                          ) : null,
                        }}
                        onChange={(e) => {
                          if (eanLocked) return;
                          if (!hasEanValue && e.target.value.trim()) {
                            markEanFieldUnlocked(eanKey);
                          }
                          updateDataSku(globalIdx, { ean13: e.target.value });
                        }}
                        onBlur={(e) => {
                          if (e.currentTarget.value.trim()) {
                            lockEanField(eanKey);
                          }
                        }}
                        fullWidth
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        size="small"
                        label="Vikt"
                        value={sku.weight ?? ''}
                        disabled={readOnly}
                        onChange={(e) =>
                          updateDataSku(globalIdx, {
                            weight: numberValue(e.target.value),
                          })
                        }
                        fullWidth
                      />
                    </Stack>
                    {isTwoDim && attribute3Options.length > 0 && (
                      <TextField
                        select
                        size="small"
                        label="Längd"
                        value={sku.attribute3Code || ''}
                        disabled={readOnly}
                        onChange={(e) =>
                          updateDataSku(globalIdx, {
                            attribute3Code: e.target.value || null,
                          })
                        }
                        sx={{ maxWidth: 200 }}
                      >
                        <MenuItem value="">–</MenuItem>
                        {attribute3Options
                          .filter(
                            (opt) =>
                              !String(opt.code || '')
                                .trim()
                                .startsWith('"'),
                          )
                          .map((opt) => (
                            <MenuItem key={opt.code} value={opt.code}>
                              {opt.code}
                            </MenuItem>
                          ))}
                      </TextField>
                    )}
                  </Stack>
                  {pos < dataSkus.length - 1 && <Divider sx={{ mt: 2.5 }} />}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDataDialogColor(null)}>Klar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SizeMatrix;
